/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React from 'react';
import { NoteCollection, ChordFingering } from '../api';
import { getIntervalColor, getIntervalColorForSkillLevel, getFingeringColor, getChordTypeColor } from '../config/colorScheme';
import { useSkillLevel } from '../context/SkillLevelContext';



interface FretPosition {
  string: number;
  fret: number;
  note: string;
}

interface FretboardProps {
  noteCollection: NoteCollection | null;
  isVertical: boolean;
  strings: number;
  frets: number;
  tuning: string[];
  showNoteNames: boolean;
  fretRange?: { start: number; end: number };
  selectedFingering?: ChordFingering | null;
  selectedDiatonicChord?: any | null;
  chordHighlightType?: 'fingering' | 'diatonic' | null;
  // Interactive ear training props
  interactive?: boolean;
  onFretClick?: (position: FretPosition) => void;
  highlightedPositions?: FretPosition[];
  rootNotePosition?: FretPosition | null;
  userAnswers?: FretPosition[];
  correctUserAnswers?: FretPosition[];
  incorrectUserAnswers?: FretPosition[];
  showFeedback?: boolean;
  // Jam Track / Timeline props
  currentChordNotes?: string[];
  jamTrackMode?: boolean;
  // Scale pattern animation
  currentAnimatedNote?: { stringIndex: number; fret: number; note: string; interval: string } | null;
}

const Fretboard: React.FC<FretboardProps> = ({
  noteCollection,
  isVertical,
  strings,
  frets,
  tuning,
  showNoteNames,
  fretRange = { start: 0, end: frets },
  selectedFingering,
  selectedDiatonicChord,
  chordHighlightType,
  // Interactive props
  interactive = false,
  onFretClick,
  highlightedPositions = [],
  rootNotePosition,
  userAnswers = [],
  correctUserAnswers = [],
  incorrectUserAnswers = [],
  showFeedback = false,
  // Jam Track props
  currentChordNotes = [],
  jamTrackMode = false,
  // Scale animation
  currentAnimatedNote = null
}) => {
  // Skill level for color scheme
  const { features } = useSkillLevel();
  const useSimplifiedColors = features.useSimplifiedColors;

  // Detect if mobile based on window width (fallback if no theme context)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Calculate visible frets based on range
  const originalStartFret = Math.max(0, Math.min(fretRange.start, frets));
  const endFret = Math.max(originalStartFret, Math.min(fretRange.end, frets));
  const startFret = originalStartFret === 0 ? 0 : originalStartFret - 1;
  const visibleFrets = Math.max(0, endFret - startFret);
  const isFullNeck = originalStartFret === 0 && endFret === frets;

  // Fix string order for horizontal layout - reverse tuning for proper orientation
  const displayTuning = isVertical ? tuning : tuning.slice().reverse();

  // Force vertical orientation on mobile for better usability
  const effectiveIsVertical = isMobile ? true : isVertical;


  // Enhanced spacing calculation with smarter mobile adjustments
  const calculateSpacing = (visibleFrets: number, isFullNeck: boolean, isMobile: boolean) => {
    // Mobile spacing optimized for better touch targets in fretboard quiz
    const baseSpacing = {
      fret: isMobile ? 35 : 40,
      string: isMobile ? 38 : 35
    };

    let fretMultiplier = 1;
    let stringMultiplier = 1;

    if (isMobile) {
      // Mobile-specific adjustments - optimized for touch and readability
      if (isFullNeck) {
        // Full 22-fret neck on mobile is very cramped - scale down significantly
        fretMultiplier = 0.55;
        stringMultiplier = 0.75;
      } else if (visibleFrets > 15) {
        // Large range (16-22 frets) - still quite cramped
        fretMultiplier = 0.6;
        stringMultiplier = 0.8;
      } else if (visibleFrets > 12) {
        // Medium-large range (13-15 frets)
        fretMultiplier = 0.7;
        stringMultiplier = 0.85;
      } else if (visibleFrets > 8) {
        // Ideal mobile range (9-12 frets) - sweet spot
        fretMultiplier = 0.85;
        stringMultiplier = 0.95;
      } else if (visibleFrets > 5) {
        // Small range (6-8 frets) - comfortable
        fretMultiplier = 1.0;
        stringMultiplier = 1.0;
      } else {
        // Very small range (1-5 frets) - can be more generous
        fretMultiplier = 1.15;
        stringMultiplier = 1.05;
      }
    } else {
      // Desktop spacing logic
      if (isFullNeck) {
        fretMultiplier = 0.85;
        stringMultiplier = 1.0;
      } else if (visibleFrets > 15) {
        fretMultiplier = 0.8;
        stringMultiplier = 0.95;
      } else if (visibleFrets > 10) {
        fretMultiplier = 0.9;
        stringMultiplier = 1;
      } else if (visibleFrets > 7) {
        fretMultiplier = 1;
        stringMultiplier = 1.05;
      } else {
        fretMultiplier = 1.2;
        stringMultiplier = 1.1;
      }
    }

    return {
      fret: Math.round(baseSpacing.fret * fretMultiplier),
      string: Math.round(baseSpacing.string * stringMultiplier)
    };
  };

  const spacing = calculateSpacing(visibleFrets, isFullNeck, isMobile);
  const fretSpacing = spacing.fret;
  const stringSpacing = spacing.string;
  const margin = isMobile ? 30 : 50;
  const firstStringPosition = margin;
  const lastStringPosition = (strings - 1) * stringSpacing + margin;

  // Adjust dimensions for fret range
  const totalWidth = effectiveIsVertical ? (strings * stringSpacing + margin * 2) : (visibleFrets * fretSpacing + margin * 2);
  const totalHeight = effectiveIsVertical ? (visibleFrets * fretSpacing + margin * 2) : (strings * stringSpacing + margin * 2);

  const fretNumbers = Array.from({ length: visibleFrets }, (_, i) => i + startFret + 1);
  const isSpecialFret = (fret: number) => [3, 5, 7, 9, 12, 15, 17, 19, 21, 24].includes(fret);

  // String thickness mapping with mobile adjustments
  const getStringThickness = (stringIndex: number, totalStrings: number): number => {
    const thicknessMap: { [key: number]: number[] } = {
      4: [3, 2.5, 1.5, 1],
      6: [3, 2.5, 2, 1.5, 1, 1],
      7: [3.5, 3, 2.5, 2, 1.5, 1, 1],
      8: [4, 3.5, 3, 2.5, 2, 1.5, 1, 1]
    };

    let thickness = 1;
    if (thicknessMap[totalStrings]) {
      thickness = thicknessMap[totalStrings][stringIndex] || 1;
    } else {
      const maxThickness = Math.min(4, Math.max(2, totalStrings * 0.5));
      const minThickness = 1;
      const normalizedPosition = stringIndex / (totalStrings - 1);
      const exponentialCurve = Math.pow(1 - normalizedPosition, 1.5);
      thickness = minThickness + (maxThickness - minThickness) * exponentialCurve;
      thickness = Math.max(1, Math.round(thickness * 10) / 10);
    }

    // Reduce thickness on mobile for better visibility
    return isMobile ? Math.max(1, thickness * 0.8) : thickness;
  };

  const renderFingeringHighlightWithCustomFingering = (
  stringIndex: number,
  fret: number,
  fingering: ChordFingering
) => {
  // (Copy the body from renderFingeringHighlight, but use this 'fingering' param instead of selectedFingering)
  if (!fingering) return null;

  const position = fingering.positions.find(
    p => p.stringIndex === stringIndex && p.fret === fret
  );
  if (!position || position.isMuted) return null;

  const noteRadius = isMobile ? 14 : 16;
  const visualStringIndex = effectiveIsVertical
    ? (strings - stringIndex)
    : (stringIndex - 1);

  let x, y;
  if (effectiveIsVertical) {
    x = visualStringIndex * stringSpacing + margin;
    if (fret === 0) {
      y = margin - (isMobile ? 20 : 25);
    } else {
      const adjustedFretIndex = fret - startFret - 1;
      y = (adjustedFretIndex + 0.5) * fretSpacing + margin;
    }
  } else {
    if (fret === 0) {
      x = margin - (isMobile ? 20 : 25);
    } else {
      const adjustedFretIndex = fret - startFret - 1;
      x = (adjustedFretIndex + 0.5) * fretSpacing + margin;
    }
    y = visualStringIndex * stringSpacing + margin;
  }

  // Use consistent coloring
  const color = position.finger ? getFingeringColor(position.finger) : getChordTypeColor('fingering');
  return (
    <g key={`diatonic-fingering-${visualStringIndex}-${fret}`}>
      <circle
        cx={x}
        cy={y}
        r={noteRadius + 4}
        fill={color}
        opacity="0.3"
        className="animate-pulse"
      />
      <circle
        cx={x}
        cy={y}
        r={noteRadius}
        fill={color}
        stroke="#ffffff"
        strokeWidth="2"
        opacity="0.9"
      />
      {position.finger && position.finger > 0 && (
        <text
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={isMobile ? "10" : "12"}
          fill="white"
          fontWeight="bold"
          className="pointer-events-none select-none"
        >
          {position.finger}
        </text>
      )}
    </g>
  );
};

  const getNoteAndInterval = (note: string) => {
    return noteCollection?.notesAndIntervals?.find(
      ni => areNotesEnharmonicallyEqual(ni.note.note, note)
    );
  };

  // Helper function to get note at a specific fret position
  const getFretNote = (fret: number, stringNote: string): string => {
    if (!stringNote || typeof stringNote !== 'string') {
      console.warn('Invalid stringNote in getFretNote:', stringNote);
      return '';
    }

    const stringIndex = getChromaticIndex(stringNote);
    if (stringIndex === -1) {
      console.warn('Unknown note in getFretNote:', stringNote);
      return stringNote;
    }

    const noteIndex = (stringIndex + fret) % 12;
    return CANONICAL_NOTES[noteIndex];
  };

  // Chord note pulse highlight rendering for jam track mode
  const renderChordNotePulse = (stringIndex: number, fret: number) => {
    if (!jamTrackMode || !currentChordNotes || currentChordNotes.length === 0) return null;

    // Convert database string index to tuning array index
    // Database: 6=low E, 1=high E
    // Array: [0=low E, 5=high E] for standard tuning ['E','A','D','G','B','E']
    const tuningIndex = stringIndex - 1; // Convert to 0-based
    const stringNote = tuning[tuningIndex];
    
    if (!stringNote || typeof stringNote !== 'string') {
      console.warn('Invalid string note at tuning index:', tuningIndex, 'value:', stringNote, 'tuning:', tuning);
      return null;
    }

    // Get the note at this position
    const note = getFretNote(fret, stringNote);
    
    // Check if this note is in the current chord
    const isMatch = currentChordNotes.some(chordNote =>
      areNotesEnharmonicallyEqual(chordNote, note)
    );
    if (!isMatch) return null;
    
    // Chord highlighting is now working correctly!

    const noteRadius = isMobile ? 14 : 16;
    // Fix visual string index - need to match how notes are actually displayed
    // Database: String 6=low E, String 1=high E  
    // Visual: Top to bottom = high E to low E
    // So String 6 should be visual index 5, String 1 should be visual index 0
    const visualStringIndex = effectiveIsVertical
      ? (stringIndex - 1)  // String 6 -> index 5, String 1 -> index 0  
      : (strings - stringIndex); // For horizontal, use the mirrored mapping

    let x, y;
    if (effectiveIsVertical) {
      x = visualStringIndex * stringSpacing + margin;
      // Match renderFingeringHighlight positioning
      if (fret === 0) {
        y = margin - (isMobile ? 20 : 25);
      } else {
        const adjustedFretIndex = fret - startFret - 1;
        y = (adjustedFretIndex + 0.5) * fretSpacing + margin;
      }
    } else {
      // Match renderFingeringHighlight positioning for horizontal
      if (fret === 0) {
        x = margin - (isMobile ? 20 : 25);
      } else {
        const adjustedFretIndex = fret - startFret - 1;
        x = (adjustedFretIndex + 0.5) * fretSpacing + margin;
      }
      y = visualStringIndex * stringSpacing + margin;
    }

    const key = `chord-pulse-${stringIndex}-${fret}`;

    return (
      <g key={key}>
        {/* Outer glow ring */}
        <circle
          cx={x}
          cy={y}
          r={noteRadius + 1}
          fill="none"
          stroke="rgba(64, 224, 255, 0.5)"
          strokeWidth="1"
          className="chord-pulse"
          style={{ pointerEvents: 'none' }}
        />
        {/* Main highlight circle - same size as original note */}
        <circle
          cx={x}
          cy={y}
          r={noteRadius}
          fill="rgba(64, 224, 255, 0.3)"
          stroke="rgba(64, 224, 255, 0.8)"
          strokeWidth="2"
          className="chord-pulse"
          style={{ pointerEvents: 'none' }}
        />
      </g>
    );
  };

  // Enhanced fingering highlight rendering
  const renderFingeringHighlight = (stringIndex: number, fret: number) => {
    if (!selectedFingering || chordHighlightType !== 'fingering') return null;

    const position = selectedFingering.positions.find(
      p => p.stringIndex === stringIndex && p.fret === fret
    );

    if (!position || position.isMuted) return null;

    const noteRadius = isMobile ? 14 : 16;
    const visualStringIndex = effectiveIsVertical ?
      (strings - stringIndex) :
      (stringIndex - 1);

    // Calculate position based on orientation
    let x, y;
    if (effectiveIsVertical) {
      x = visualStringIndex * stringSpacing + margin;
      // Fix for open strings - align with open string notes position
      if (fret === 0) {
        y = margin - (isMobile ? 20 : 25);
      } else {
        const adjustedFretIndex = fret - startFret - 1;
        y = (adjustedFretIndex + 0.5) * fretSpacing + margin;
      }
    } else {
      // Fix for horizontal layout
      if (fret === 0) {
        x = margin - (isMobile ? 20 : 25);
      } else {
        const adjustedFretIndex = fret - startFret - 1;
        x = (adjustedFretIndex + 0.5) * fretSpacing + margin;
      }
      y = visualStringIndex * stringSpacing + margin;
    }

    // Use consistent coloring - no special root note treatment
    const color = position.finger ? getFingeringColor(position.finger) : getChordTypeColor('fingering');

    return (
      <g key={`fingering-${visualStringIndex}-${fret}`}>
        {/* Glow effect for chord notes */}
        <circle
          cx={x}
          cy={y}
          r={noteRadius + 4}
          fill={color}
          opacity="0.3"
          className="animate-pulse"
        />
        
        {/* Main chord note circle - consistent styling for all notes */}
        <circle
          cx={x}
          cy={y}
          r={noteRadius}
          fill={color}
          stroke="#ffffff"
          strokeWidth="2"
          opacity="0.9"
        />

        {/* Finger number with consistent styling */}
        {position.finger && position.finger > 0 && (
          <text
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={isMobile ? "10" : "12"}
            fill="white"
            fontWeight="bold"
            className="pointer-events-none select-none"
          >
            {position.finger}
          </text>
        )}
      </g>
    );
  };

  // Scale animation highlight rendering
  const renderScaleAnimationHighlight = (stringIndex: number, fret: number) => {
    if (!currentAnimatedNote) return null;

    // Convert database stringIndex (1-6) to noteCollection array index (0-5)
    // Database: String 6 = low E, String 1 = high E
    // Array: [0] = low E, [5] = high E
    const noteCollectionArrayIndex = strings - stringIndex;

    // Check if this position matches the animated note
    const isAnimatedNote =
      currentAnimatedNote.stringIndex === noteCollectionArrayIndex &&
      currentAnimatedNote.fret === fret;

    if (!isAnimatedNote) return null;

    // Get the interval color for this note (respects skill level)
    const intervalColor = getIntervalColorForSkillLevel(currentAnimatedNote.interval, useSimplifiedColors);
    // Convert hex to rgba for transparency effects
    const hexToRgba = (hex: string, alpha: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const noteRadius = isMobile ? 16 : 20; // Larger than normal notes
    const visualStringIndex = effectiveIsVertical
      ? (strings - stringIndex)
      : (stringIndex - 1);

    let x, y;
    if (effectiveIsVertical) {
      x = visualStringIndex * stringSpacing + margin;
      if (fret === 0) {
        y = margin - (isMobile ? 20 : 25);
      } else {
        const adjustedFretIndex = fret - startFret - 1;
        y = (adjustedFretIndex + 0.5) * fretSpacing + margin;
      }
    } else {
      if (fret === 0) {
        x = margin - (isMobile ? 20 : 25);
      } else {
        const adjustedFretIndex = fret - startFret - 1;
        x = (adjustedFretIndex + 0.5) * fretSpacing + margin;
      }
      y = visualStringIndex * stringSpacing + margin;
    }

    return (
      <g key={`scale-animation-${visualStringIndex}-${fret}`}>
        {/* Outer pulsing glow */}
        <circle
          cx={x}
          cy={y}
          r={noteRadius + 6}
          fill="none"
          stroke={hexToRgba(intervalColor, 0.6)}
          strokeWidth="3"
          className="animate-pulse"
          style={{ pointerEvents: 'none' }}
        />
        {/* Main highlight circle */}
        <circle
          cx={x}
          cy={y}
          r={noteRadius}
          fill={hexToRgba(intervalColor, 0.5)}
          stroke={hexToRgba(intervalColor, 0.9)}
          strokeWidth="3"
          style={{ pointerEvents: 'none' }}
        />
        {/* Note name */}
        <text
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={isMobile ? "12" : "14"}
          fill="white"
          fontWeight="bold"
          className="pointer-events-none select-none"
          style={{ textShadow: '0 0 3px rgba(0,0,0,0.8)' }}
        >
          {currentAnimatedNote.note}
        </text>
      </g>
    );
  };

  // Render diatonic chord highlights
  const renderDiatonicChordHighlight = (stringIndex: number, fret: number) => {
    if (
      !selectedDiatonicChord ||
      chordHighlightType !== 'diatonic' ||
      !selectedDiatonicChord.chord ||
      !Array.isArray(selectedDiatonicChord.chord.chordNotes)
    ) {
      return null;
    }

    // Use the correct shape: grab the chordNotes from .chord
    const noteAtPosition = getFretNote(displayTuning[stringIndex - 1], fret);
    const isInChord = selectedDiatonicChord.chord.chordNotes.some((chordNote: string) =>
      areNotesEnharmonicallyEqual(chordNote, noteAtPosition)
    );

    if (!isInChord) return null;

    const noteRadius = isMobile ? 13 : 15;
    const color = getChordTypeColor('diatonic');
    const adjustedFretIndex = fret - startFret - 1;

    const x = effectiveIsVertical
      ? (stringIndex - 1) * stringSpacing + margin
      : (adjustedFretIndex + 0.5) * fretSpacing + margin;

    const y = effectiveIsVertical
      ? (adjustedFretIndex + 0.5) * fretSpacing + margin
      : (stringIndex - 1) * stringSpacing + margin;

    return (
      <g key={`diatonic-${stringIndex}-${fret}`}>
        <circle
          cx={x}
          cy={y}
          r={noteRadius}
          fill={color}
          stroke="#ffffff"
          strokeWidth="2"
          opacity="0.8"
        />
        <text
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={isMobile ? "8" : "9"}
          fill="white"
          fontWeight="bold"
          className="pointer-events-none select-none"
        >
          {noteAtPosition}
        </text>
      </g>
    );
  };

  // Handle fretboard clicks for interactive mode
  const handleFretClick = (visualStringIndex: number, fret: number) => {
    console.log('handleFretClick called:', { visualStringIndex, fret, interactive, hasOnFretClick: !!onFretClick });
    if (!interactive || !onFretClick) return;
    
    // visualStringIndex is 1-based from the caller, represents position in displayTuning
    const note = getFretNote(displayTuning[visualStringIndex - 1], fret);
    
    // Convert visual string index to original tuning index
    // In horizontal mode, displayTuning is reversed, so we need to map back to original indices
    let originalStringIndex: number;
    if (effectiveIsVertical) {
      originalStringIndex = visualStringIndex; // No conversion needed in vertical mode
    } else {
      // In horizontal mode, convert from displayTuning index to original tuning index
      // displayTuning is tuning.slice().reverse(), so:
      // visualStringIndex 1 (first in displayTuning) = last string in original tuning
      originalStringIndex = strings - visualStringIndex + 1;
    }
    
    const position: FretPosition = {
      string: originalStringIndex, // Use original tuning index
      fret,
      note
    };
    
    onFretClick(position);
  };

  // Render interactive highlights
  const renderInteractiveHighlights = (visualStringIndex: number, fret: number) => {
    if (!interactive) return null;

    const noteRadius = isMobile ? 13 : 15;
    const adjustedFretIndex = fret - startFret - 1;
    
    // Calculate position using visual string index
    let x, y;
    if (effectiveIsVertical) {
      x = (visualStringIndex - 1) * stringSpacing + margin;
      if (fret === 0) {
        y = margin - (isMobile ? 20 : 25);
      } else {
        y = (adjustedFretIndex + 0.5) * fretSpacing + margin;
      }
    } else {
      if (fret === 0) {
        x = margin - (isMobile ? 20 : 25);
      } else {
        x = (adjustedFretIndex + 0.5) * fretSpacing + margin;
      }
      y = (visualStringIndex - 1) * stringSpacing + margin;
    }

    // Convert visual string index to original tuning index for comparisons
    let originalStringIndex: number;
    if (effectiveIsVertical) {
      originalStringIndex = visualStringIndex; // No conversion needed in vertical mode
    } else {
      // In horizontal mode, convert from displayTuning index to original tuning index
      originalStringIndex = strings - visualStringIndex + 1;
    }

    // Check if this position matches special states using original string index
    const isRootNote = rootNotePosition && 
      rootNotePosition.string === originalStringIndex && 
      rootNotePosition.fret === fret;
    
    
    const isUserAnswer = userAnswers.some(answer => 
      answer.string === originalStringIndex && answer.fret === fret);
    
    const isCorrectUserAnswer = correctUserAnswers.some(answer => 
      answer.string === originalStringIndex && answer.fret === fret);
    
    const isIncorrectUserAnswer = incorrectUserAnswers.some(answer => 
      answer.string === originalStringIndex && answer.fret === fret);
    
    const isHighlighted = highlightedPositions.some(pos =>
      pos.string === originalStringIndex && pos.fret === fret);

    // Root note highlight (green)
    if (isRootNote) {
      return (
        <g key={`root-${visualStringIndex}-${fret}`} style={{ pointerEvents: 'none' }}>
          <circle
            cx={x}
            cy={y}
            r={noteRadius + 2}
            fill="none"
            stroke="#22c55e"
            strokeWidth="3"
            opacity="0.8"
          />
          <circle
            cx={x}
            cy={y}
            r={noteRadius}
            fill="#22c55e"
            stroke="white"
            strokeWidth="2"
            opacity="0.9"
          />
          <text
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={isMobile ? "10" : "12"}
            fill="white"
            fontWeight="bold"
            className="pointer-events-none select-none"
          >
            R
          </text>
        </g>
      );
    }

    // Correct user answer highlight (green)
    if (isCorrectUserAnswer) {
      return (
        <g key={`correct-user-${visualStringIndex}-${fret}`} style={{ pointerEvents: 'none' }}>
          <circle
            cx={x}
            cy={y}
            r={noteRadius + 2}
            fill="none"
            stroke="#22c55e"
            strokeWidth="3"
            opacity="0.8"
          />
          <circle
            cx={x}
            cy={y}
            r={noteRadius}
            fill="#22c55e"
            stroke="white"
            strokeWidth="2"
            opacity="0.9"
          />
          <text
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={isMobile ? "8" : "10"}
            fill="white"
            fontWeight="bold"
            className="pointer-events-none select-none"
          >
            {getFretNote(displayTuning[visualStringIndex - 1], fret)}
          </text>
        </g>
      );
    }

    // Incorrect user answer highlight (red)
    if (isIncorrectUserAnswer) {
      return (
        <g key={`incorrect-user-${visualStringIndex}-${fret}`} style={{ pointerEvents: 'none' }}>
          <circle
            cx={x}
            cy={y}
            r={noteRadius + 2}
            fill="none"
            stroke="#ef4444"
            strokeWidth="3"
            opacity="0.8"
          />
          <circle
            cx={x}
            cy={y}
            r={noteRadius}
            fill="#ef4444"
            stroke="white"
            strokeWidth="2"
            opacity="0.9"
          />
          <text
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={isMobile ? "8" : "10"}
            fill="white"
            fontWeight="bold"
            className="pointer-events-none select-none"
          >
            {getFretNote(displayTuning[visualStringIndex - 1], fret)}
          </text>
        </g>
      );
    }

    // User answer highlight (blue) - fallback for when not using correct/incorrect
    if (isUserAnswer) {
      return (
        <g key={`user-${visualStringIndex}-${fret}`} style={{ pointerEvents: 'none' }}>
          <circle
            cx={x}
            cy={y}
            r={noteRadius + 2}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="3"
            opacity="0.8"
          />
          <circle
            cx={x}
            cy={y}
            r={noteRadius}
            fill="#3b82f6"
            stroke="white"
            strokeWidth="2"
            opacity="0.9"
          />
          <text
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={isMobile ? "8" : "10"}
            fill="white"
            fontWeight="bold"
            className="pointer-events-none select-none"
          >
            {getFretNote(displayTuning[visualStringIndex - 1], fret)}
          </text>
        </g>
      );
    }

    // Highlighted positions (for correct answers, etc.)
    if (isHighlighted) {
      return (
        <g key={`highlight-${visualStringIndex}-${fret}`} style={{ pointerEvents: 'none' }}>
          <circle
            cx={x}
            cy={y}
            r={noteRadius + 2}
            fill="none"
            stroke="#f59e0b"
            strokeWidth="3"
            opacity="0.8"
          />
          <circle
            cx={x}
            cy={y}
            r={noteRadius}
            fill="#f59e0b"
            stroke="white"
            strokeWidth="2"
            opacity="0.9"
          />
        </g>
      );
    }

    return null;
  };

  return (
    <div className={`flex ${effectiveIsVertical ? 'flex-col' : 'flex-row'} items-center justify-center p-4 bg-background`}>
      <svg
        width={totalWidth}
        height={totalHeight}
        className="border border-border rounded-lg shadow-lg bg-card"
        onPointerUp={() => console.log('SVG received pointerUp')}
        onClick={() => console.log('SVG received click')}
      >
        {/* Background */}
        <rect
          width={totalWidth}
          height={totalHeight}
          fill="white"
        />

          {/* Guitar Nut - only show if starting from fret 0 */}
          {originalStartFret === 0 && (
            effectiveIsVertical ? (
              <rect
                x={margin - 5}
                y={margin - 8}
                width={(strings - 1) * stringSpacing + 10} // Added +10 to extend past last string
                height="6"
                fill="#8B4513"
              />
            ) : (
              <rect
                x={margin - 8}
                y={margin - 5}
                width="6"
                height={(strings - 1) * stringSpacing + 10} // Added +10 to extend past last string
                fill="#8B4513"
              />
            )
          )}


        {/* Fret lines */}
        {fretNumbers.map((fret, index) => {
          if (effectiveIsVertical) {
            const y = (index + 1) * fretSpacing + margin;
            return (
              <line
                key={`fret-${fret}`}
                x1={margin}
                y1={y}
                x2={(strings - 1) * stringSpacing + margin} // Fixed: use (strings - 1)
                y2={y}
                stroke="#000"
                strokeWidth="2"
              />
            );
          } else {
            const x = (index + 1) * fretSpacing + margin;
            return (
              <line
                key={`fret-${fret}`}
                x1={x}
                y1={margin}
                x2={x}
                y2={(strings - 1) * stringSpacing + margin} // Fixed: use (strings - 1) for horizontal too
                stroke="#000"
                strokeWidth="2"
              />
            );
          }
        })}


        {/* String lines with variable thickness */}
        {displayTuning.slice(0, strings).map((_, stringIndex) => {
          // For horizontal layout, we need to adjust thickness calculation since strings are reversed
          const thicknessIndex = effectiveIsVertical ? stringIndex : (strings - 1 - stringIndex);
          const thickness = getStringThickness(thicknessIndex, strings);

          if (effectiveIsVertical) {
            const x = stringIndex * stringSpacing + margin;
            return (
              <line
                key={`string-${stringIndex}`}
                x1={x}
                y1={margin}
                x2={x}
                y2={visibleFrets * fretSpacing + margin}
                stroke="#000"
                strokeWidth={thickness}
              />
            );
          } else {
            const y = stringIndex * stringSpacing + margin;
            return (
              <line
                key={`string-${stringIndex}`}
                x1={margin}
                y1={y}
                x2={visibleFrets * fretSpacing + margin}
                y2={y}
                stroke="#000"
                strokeWidth={thickness}
              />
            );
          }
        })}

        {/* Fret markers with dots and numbers */}
        {fretNumbers.map((fret, index) => {
        if (!isSpecialFret(fret)) return null;

        const isDoubleDot = fret === 12 || fret === 24;
        const dotRadius = isMobile ? 3 : 4;
        const largeDotRadius = isMobile ? 4 : 5;

        if (effectiveIsVertical) {
            const centerX = (firstStringPosition + lastStringPosition) / 2;
            const y = (index + 0.5) * fretSpacing + margin;

            return (
            <g key={`marker-${fret}`}>
                {/* Fret dots */}
                {isDoubleDot ? (
                <>
                    <circle cx={centerX - 15} cy={y} r={largeDotRadius} fill="#666" />
                    <circle cx={centerX + 15} cy={y} r={largeDotRadius} fill="#666" />
                </>
                ) : (
                <circle cx={centerX} cy={y} r={dotRadius} fill="#666" />
                )}

                {/* Fret numbers - positioned further left and darker */}
                <text
                    x={margin - 25} // Moved further away (was -15)
                    y={y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={isMobile ? "10" : isFullNeck ? "10" : "12"} // Smaller font for full neck
                    fill="#333" // Darker color (was #666)
                    fontWeight="600" // Added for better visibility
                    className="pointer-events-none select-none"
                >
                    {fret}
                </text>
            </g>
            );
        } else {
            const x = (index + 0.5) * fretSpacing + margin;
            const centerY = (firstStringPosition + lastStringPosition) / 2;

            return (
            <g key={`marker-${fret}`}>
                {/* Fret dots */}
                {isDoubleDot ? (
                <>
                    <circle cx={x} cy={centerY - 15} r={largeDotRadius} fill="#666" />
                    <circle cx={x} cy={centerY + 15} r={largeDotRadius} fill="#666" />
                </>
                ) : (
                <circle cx={x} cy={centerY} r={dotRadius} fill="#666" />
                )}

                {/* Fret numbers - positioned further above and darker */}
                <text
                    x={x}
                    y={margin - 25} // Moved further away (was -15)
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={isMobile ? "10" : isFullNeck ? "10" : "12"} // Smaller font for full neck
                    fill="#333" // Darker color (was #666)
                    fontWeight="600" // Added for better visibility
                    className="pointer-events-none select-none"
                >
                    {fret}
                </text>
            </g>
            );
        }
        })}



        {/* Open string notes - only show if starting from fret 0 */}
        {originalStartFret === 0 && displayTuning.slice(0, strings).map((stringNote, stringIndex) => {
          const noteAndInterval = getNoteAndInterval(stringNote);
          const circleRadius = isMobile ? 12 : 15;

          if (effectiveIsVertical) {
            const x = stringIndex * stringSpacing + margin;
            const y = margin - (isMobile ? 20 : 25);

            return (
              <g key={`open-${stringIndex}`}>
                <circle
                  cx={x}
                  cy={y}
                  r={circleRadius}
                  fill={noteAndInterval ? getIntervalColorForSkillLevel(noteAndInterval.interval.abbreviation, useSimplifiedColors) : '#e6f7ff'}
                  stroke="#4a90e2"
                  strokeWidth="2"
                  className="drop-shadow-sm"
                />
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={isMobile ? "12" : "14"}
                  fill="black"
                  fontWeight="bold"
                  className="pointer-events-none select-none"
                >
                  {stringNote}
                </text>
              </g>
            );
          } else {
            const x = margin - (isMobile ? 20 : 25);
            const y = stringIndex * stringSpacing + margin;

            return (
              <g key={`open-${stringIndex}`}>
                <circle
                  cx={x}
                  cy={y}
                  r={circleRadius}
                  fill={noteAndInterval ? getIntervalColorForSkillLevel(noteAndInterval.interval.abbreviation, useSimplifiedColors) : '#e6f7ff'}
                  stroke="#4a90e2"
                  strokeWidth="2"
                  className="drop-shadow-sm"
                />
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={isMobile ? "12" : "14"}
                  fill="black"
                  fontWeight="bold"
                  className="pointer-events-none select-none"
                >
                  {stringNote}
                </text>
              </g>
            );
          }
        })}

        {/* Notes on frets - hidden during animation */}
        {!currentAnimatedNote && noteCollection?.notesAndIntervals?.map((noteAndInterval, noteIndex) => {
          return displayTuning.slice(0, strings).map((stringNote, stringIndex) => {
            const calculatedFret = getFretNumber(stringNote, noteAndInterval.note.note);
            const notesToRender = [];

            for (let octave = 0; octave <= Math.floor(frets / 12) + 1; octave++) {
              const fretPosition = calculatedFret + (octave * 12);
              if (fretPosition >= startFret + 1 && fretPosition <= endFret) {
                notesToRender.push(fretPosition);
              }
            }

            return notesToRender.map((fretPos) => {
              const adjustedFretIndex = fretPos - startFret - 1;
              const isRootNote = noteAndInterval.interval.abbreviation === 'R' ||
                noteAndInterval.interval.abbreviation === '1';
              const noteRadius = isMobile ? (isRootNote ? 11 : 10) : (isRootNote ? 14 : 12);
              const fontSize = isMobile ? "9" : "10";

              if (effectiveIsVertical) {
                const x = stringIndex * stringSpacing + margin;
                const y = (adjustedFretIndex + 0.5) * fretSpacing + margin;

                return (
                  <g key={`note-${noteIndex}-${stringIndex}-${fretPos}`}>
                    <circle
                      cx={x}
                      cy={y}
                      r={noteRadius}
                      fill={getIntervalColorForSkillLevel(noteAndInterval.interval.abbreviation, useSimplifiedColors)}
                      stroke={isRootNote ? "#000" : "none"}
                      strokeWidth={isRootNote ? "2" : "0"}
                      className="drop-shadow-sm"
                    />
                    {showNoteNames && (
                      <text
                        x={x}
                        y={y}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={fontSize}
                        fill="white"
                        fontWeight="bold"
                        className="pointer-events-none select-none drop-shadow-sm"
                      >
                        {noteAndInterval.note.note}
                      </text>
                    )}
                  </g>
                );
              } else {
                const x = (adjustedFretIndex + 0.5) * fretSpacing + margin;
                const y = stringIndex * stringSpacing + margin;

                return (
                  <g key={`note-${noteIndex}-${stringIndex}-${fretPos}`}>
                    <circle
                      cx={x}
                      cy={y}
                      r={noteRadius}
                      fill={getIntervalColorForSkillLevel(noteAndInterval.interval.abbreviation, useSimplifiedColors)}
                      stroke={isRootNote ? "#000" : "none"}
                      strokeWidth={isRootNote ? "2" : "0"}
                      className="drop-shadow-sm"
                    />
                    {showNoteNames && (
                      <text
                        x={x}
                        y={y}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={fontSize}
                        fill="white"
                        fontWeight="bold"
                        className="pointer-events-none select-none drop-shadow-sm"
                      >
                        {noteAndInterval.note.note}
                      </text>
                    )}
                  </g>
                );
              }
            });
          });
        })}

        {/* Fingering Highlights - render on top of scale notes */}
        {selectedFingering && chordHighlightType === 'fingering' &&
          Array.from({length: strings}).map((_, visualIndex) =>
            Array.from({length: visibleFrets + 1}).map((_, fretIndex) => {
              const fret = startFret + fretIndex; // Includes fret 0
              if (originalStartFret > 0 && fret < originalStartFret) {
                return null;
              }
              const databaseStringIndex = strings - visualIndex; // This gives us 6,5,4,3,2,1
              return renderFingeringHighlight(databaseStringIndex, fret);
            })
          )
        }

        {/* Diatonic Chord Highlights - render on top of scale notes */}
        {/* Diatonic Chord Highlights - EXACT fingering if chosen, generic notes otherwise */}
        {selectedDiatonicChord && chordHighlightType === 'diatonic' &&
          (
            selectedDiatonicChord.fingering
            // Render exactly like a Chord-mode fingering for this chord's fingering
            ? Array.from({length: strings}).map((_, visualIndex) =>
                Array.from({length: visibleFrets + 1}).map((_, fretIndex) => {
                  const fret = startFret + fretIndex; // Includes fret 0
                  if (originalStartFret > 0 && fret < originalStartFret) {
                    return null;
                  }
                  const databaseStringIndex = strings - visualIndex;
                  // Use renderFingeringHighlight and pass in selectedDiatonicChord.fingering
                  // Trick: temporarily treat as "fingering highlight"!
                  return renderFingeringHighlightWithCustomFingering(
                    databaseStringIndex,
                    fret,
                    selectedDiatonicChord.fingering
                  );
                })
              )
            // Otherwise, fallback to generic "in chord" dots
            : Array.from({length: strings}).map((_, visualIndex) =>
                Array.from({length: visibleFrets}).map((_, fretIndex) => {
                  const fret = startFret + fretIndex + 1;
                  const databaseStringIndex = strings - visualIndex;
                  return renderDiatonicChordHighlight(databaseStringIndex, fret);
                })
              )
          )
        }

        {/* Chord Note Pulse Highlights - for jam track mode */}
        {jamTrackMode && currentChordNotes && currentChordNotes.length > 0 &&
          Array.from({length: strings}).map((_, visualIndex) =>
            Array.from({length: visibleFrets + 1}).map((_, fretIndex) => {
              const fret = startFret + fretIndex; // Includes fret 0
              if (originalStartFret > 0 && fret < originalStartFret) {
                return null;
              }
              const databaseStringIndex = strings - visualIndex; // This gives us 6,5,4,3,2,1
              return renderChordNotePulse(databaseStringIndex, fret);
            })
          )
        }

        {/* Scale Animation Highlights - for scale pattern animation */}
        {currentAnimatedNote &&
          Array.from({length: strings}).map((_, visualIndex) =>
            Array.from({length: visibleFrets + 1}).map((_, fretIndex) => {
              const fret = startFret + fretIndex; // Includes fret 0
              if (originalStartFret > 0 && fret < originalStartFret) {
                return null;
              }
              const databaseStringIndex = strings - visualIndex; // This gives us 6,5,4,3,2,1
              return renderScaleAnimationHighlight(databaseStringIndex, fret);
            })
          )
        }

        {/* Interactive Layer - Clickable areas and highlights */}
        {interactive && (
          <>
            {/* Invisible clickable areas for all fret positions */}
            {Array.from({ length: strings }).map((_, visualIndex) => 
              Array.from({ length: visibleFrets + 1 }).map((_, fretIndex) => {
                const fret = startFret + fretIndex; // Includes fret 0
                if (originalStartFret > 0 && fret < originalStartFret) {
                  return null;
                }
                const stringIndex = visualIndex + 1; // 1-indexed for database
                
                const clickRadius = isMobile ? 18 : 22;
                
                let x, y;
                if (effectiveIsVertical) {
                  x = visualIndex * stringSpacing + margin;
                  if (fret === 0) {
                    y = margin - (isMobile ? 20 : 25);
                  } else {
                    const adjustedFretIndex = fret - startFret - 1;
                    y = (adjustedFretIndex + 0.5) * fretSpacing + margin;
                  }
                } else {
                  if (fret === 0) {
                    x = margin - (isMobile ? 20 : 25);
                  } else {
                    const adjustedFretIndex = fret - startFret - 1;
                    x = (adjustedFretIndex + 0.5) * fretSpacing + margin;
                  }
                  y = visualIndex * stringSpacing + margin;
                }

                return (
                  <circle
                    key={`clickable-${stringIndex}-${fret}`}
                    cx={x}
                    cy={y}
                    r={clickRadius}
                    fill="transparent"
                    stroke="none"
                    style={{ touchAction: 'none' }}
                    className="cursor-pointer hover:stroke-gray-400 hover:stroke-2 hover:fill-gray-100 hover:fill-opacity-20"
                    onPointerUp={(e) => {
                      e.preventDefault();
                      handleFretClick(stringIndex, fret);
                    }}
                  />
                );
              })
            )}

            {/* Interactive highlights - render on top */}
            {Array.from({ length: strings }).map((_, visualIndex) => 
              Array.from({ length: visibleFrets + 1 }).map((_, fretIndex) => {
                const fret = startFret + fretIndex; // Includes fret 0
                if (originalStartFret > 0 && fret < originalStartFret) {
                  return null;
                }
                const stringIndex = visualIndex + 1; // 1-indexed for database
                
                return renderInteractiveHighlights(stringIndex, fret);
              })
            )}
          </>
        )}

      </svg>
    </div>
  );
};

// Helper functions
const CANONICAL_NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;
const BASE_NOTE_OFFSETS: Record<string, number> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11
};

const getChromaticIndex = (noteName: string): number => {
  if (!noteName) {
    return -1;
  }

  const trimmed = noteName.trim();
  if (trimmed.length === 0) {
    return -1;
  }

  const letter = trimmed[0].toUpperCase();
  const baseOffset = BASE_NOTE_OFFSETS[letter];
  if (baseOffset === undefined) {
    return -1;
  }

  let semitone = baseOffset;
  for (let i = 1; i < trimmed.length; i++) {
    const char = trimmed[i];
    if (char === '#' || char === '♯') {
      semitone += 1;
    } else if (char === 'b' || char === '♭') {
      semitone -= 1;
    } else if (char === 'n' || char === '♮' || char === ' ') {
      continue;
    } else if (char >= '0' && char <= '9') {
      break;
    } else {
      break;
    }
  }

  semitone = ((semitone % 12) + 12) % 12;
  return semitone;
};

const areNotesEnharmonicallyEqual = (first: string, second: string): boolean => {
  if (!first || !second) {
    return first === second;
  }

  const firstIndex = getChromaticIndex(first);
  const secondIndex = getChromaticIndex(second);

  if (firstIndex === -1 || secondIndex === -1) {
    return first === second;
  }

  return firstIndex === secondIndex;
};

const getFretNumber = (stringNote: string, noteName: string): number => {
  const stringIndex = getChromaticIndex(stringNote);
  const noteIndex = getChromaticIndex(noteName);

  if (stringIndex === -1 || noteIndex === -1) {
    return -1;
  }

  return (noteIndex - stringIndex + 12) % 12;
};

export default Fretboard;
export type { FretPosition };
